import { IOption } from '../../base/base-forms/interface';
import {
    IClientMetricData,
    IClientTimeToHireJobData,
} from '../interfaces/reports.interfaces';

type MetricKey = {
    [K in keyof IClientTimeToHireJobData]: IClientTimeToHireJobData[K] extends IClientMetricData
        ? K
        : never;
}[keyof IClientTimeToHireJobData];

export enum FunnelView {
    COMPANY = 'company',
    JOB = 'job',
    RECRUITER = 'recruiter',
}

export enum ReportPeriod {
    LIFETIME = 'lifetime',
    YEAR = 'year',
    QUARTER = 'quarter',
    MONTH = 'month',
    WEEK = 'week',
    CUSTOM = 'custom',
}

export const TIME_FRAME_OPTIONS: IOption[] = [
    { label: 'reportsPage.filter.lifetime', value: ReportPeriod.LIFETIME },
    { label: 'reportsPage.filter.lastYear', value: ReportPeriod.YEAR },
    { label: 'reportsPage.filter.lastQuarter', value: ReportPeriod.QUARTER },
    { label: 'reportsPage.filter.lastMonth', value: ReportPeriod.MONTH },
    { label: 'reportsPage.filter.lastWeek', value: ReportPeriod.WEEK },
    { label: 'reportsPage.filter.custom', value: ReportPeriod.CUSTOM },
];

export enum TimeToHireChartType {
    BAR = 'bar',
    LINE = 'line',
    PIE = 'pie',
}

export const TIME_TO_HIRE_METRIC_KEYS: readonly MetricKey[] = [
    'unprocessedToDeclined',
    'unprocessedToInReview',
    'timeInReview',
    'hiringTimeAverage',
    'timeToFirstApplication',
] as const;

export const TIME_TO_HIRE_METRIC_LABELS: Record<string, string> = {
    unprocessedToDeclined: 'reportsPage.timeToHireReport.metrics.newToReject',
    unprocessedToInReview: 'reportsPage.timeToHireReport.metrics.newInProgress',
    timeInReview: 'reportsPage.timeToHireReport.metrics.avgTimeInProcess',
    hiringTimeAverage: 'reportsPage.timeToHireReport.metrics.avgHiringTime',
    timeToFirstApplication: 'reportsPage.timeToHireReport.metrics.timeToFirstApplication',
};

export const RADIAN = Math.PI / 180;

export const TIME_TO_HIRE_CHART_COLORS = ['#7E00FF', '#00B341', '#FF8C00'];

export const TIME_TO_HIRE_PIE_COLORS: Record<MetricKey, string> = {
    unprocessedToDeclined: '#FF7F00',
    unprocessedToInReview: '#B5B5B5',
    timeInReview: '#68B7F7',
    hiringTimeAverage: '#06B217',
    timeToFirstApplication: '#7E00FF',
};

export interface ITimeToHireMetricCardConfig {
    titleKey: string;
    subtitleKey: string;
    dataField: typeof TIME_TO_HIRE_METRIC_KEYS[number];
    isFullTimeline?: boolean;
}

export const TIME_TO_HIRE_METRIC_CARD_CONFIGS: ITimeToHireMetricCardConfig[] = [
    {
        titleKey: 'reportsPage.timeToHireReport.newToReject',
        subtitleKey: 'reportsPage.timeToHireReport.newToRejectDesc',
        dataField: 'unprocessedToDeclined',
    },
    {
        titleKey: 'reportsPage.timeToHireReport.newToInProgress',
        subtitleKey: 'reportsPage.timeToHireReport.newToInProgressDesc',
        dataField: 'unprocessedToInReview',
    },
    {
        titleKey: 'reportsPage.timeToHireReport.inProcess',
        subtitleKey: 'reportsPage.timeToHireReport.inProcessDesc',
        dataField: 'timeInReview',
    },
    {
        titleKey: 'reportsPage.timeToHireReport.hiringTime',
        subtitleKey: 'reportsPage.timeToHireReport.hiringTimeDesc',
        dataField: 'hiringTimeAverage',
        isFullTimeline: true,
    },
    {
        titleKey: 'reportsPage.timeToHireReport.timeToFirstApplication',
        subtitleKey: 'reportsPage.timeToHireReport.timeToFirstApplicationDesc',
        dataField: 'timeToFirstApplication',
    },
];

export const REPORTS_COLORS = [
    '#7E00FF',
    '#9B30FF',
    '#B366FF',
    '#CC99FF',
    '#E0CCFF',
];

export enum ReportTab {
    TIME_TO_HIRE = 'timeToHire',
    RECRUITMENT_FUNNEL = 'recruitmentFunnel',
    APPLY_TO_HIRE_RATIO = 'applyToHireRatio',
    SOURCE_OF_HIRE = 'sourceOfHire',
    GOALS_VS_ACTUALS = 'goalsVsActuals',
}

export const FUNNEL_LABEL_MAP: Record<string, string> = {
    New: 'reportsPage.funnelLabels.newCandidates',
    'In Progress': 'reportsPage.funnelLabels.candidatesInProgress',
    Hired: 'reportsPage.funnelLabels.hiredCandidates',
    Rejected: 'reportsPage.funnelLabels.rejectedCandidates',
    Withdrawn: 'reportsPage.funnelLabels.withdrawnCandidates',
};

export const REPORT_TABS = [
    {
        key: ReportTab.TIME_TO_HIRE,
        labelKey: 'reportsPage.timeToHire',
    },
    {
        key: ReportTab.RECRUITMENT_FUNNEL,
        labelKey: 'reportsPage.recruitmentFunnel',
    },
    {
        key: ReportTab.APPLY_TO_HIRE_RATIO,
        labelKey: 'reportsPage.applyToHireRatio',
    },
    {
        key: ReportTab.SOURCE_OF_HIRE,
        labelKey: 'reportsPage.sourceOfHire',
    },
    {
        key: ReportTab.GOALS_VS_ACTUALS,
        labelKey: 'reportsPage.goalsVsActuals',
    },
];

export enum SourceOfHireViewType {
    TABLE = 'table',
    PIE = 'pie',
    BAR = 'bar',
}

export type SourceOfHireTop3StatsKey =
    | 'topThreeClicks'
    | 'topThreeApplicants'
    | 'topThreeHired'
    | 'topThreeSuccessRate';

export interface ISourceOfHireTop3Config {
    statsKey: SourceOfHireTop3StatsKey;
    titleKey: string;
    isPercent: boolean;
}

export const SOURCE_OF_HIRE_TOP3_CONFIGS: ISourceOfHireTop3Config[] = [
    {
        statsKey: 'topThreeClicks',
        titleKey: 'reportsPage.sourceOfHireReport.clicks',
        isPercent: false,
    },
    {
        statsKey: 'topThreeApplicants',
        titleKey: 'reportsPage.sourceOfHireReport.applicants',
        isPercent: false,
    },
    {
        statsKey: 'topThreeHired',
        titleKey: 'reportsPage.sourceOfHireReport.hired',
        isPercent: false,
    },
    {
        statsKey: 'topThreeSuccessRate',
        titleKey: 'reportsPage.sourceOfHireReport.successRate',
        isPercent: true,
    },
];

export enum HiringSourcesTableFilterIds {
    sourceTitle = 'source_title',
    clicks = 'clicks',
    applications = 'applications',
    screened = 'screened',
    hired = 'hired',
    clickApplyPercent = 'click_apply_percent',
    applyScreenPercent = 'apply_screen_percent',
    overallConversion = 'overall_conversion',
}

export const SOURCE_OF_HIRE_CHART_COLORS = [
    '#a34aff',
    '#d7a5ff',
    '#00b8d9',
    '#36b37e',
    '#ffab00',
    '#3366ff',
    '#ff5630',
    '#6554c0',
];

export type SourceOfHireDistributionMetric =
    | 'clicks'
    | 'applications'
    | 'screened'
    | 'hired'
    | 'clickApplyPercent'
    | 'applyScreenPercent'
    | 'overallConversion';

export const SOURCE_OF_HIRE_DISTRIBUTION_METRICS: {
    metric: SourceOfHireDistributionMetric;
    labelKey: string;
    shortLabelKey: string;
    isPercent: boolean;
}[] = [
    {
        metric: 'clicks',
        labelKey: 'reportsPage.sourceOfHireReport.clicksDistribution',
        shortLabelKey: 'reportsPage.sourceOfHireReport.clicks',
        isPercent: false,
    },
    {
        metric: 'applications',
        labelKey: 'reportsPage.sourceOfHireReport.applicationsDistribution',
        shortLabelKey: 'reportsPage.sourceOfHireReport.applications',
        isPercent: false,
    },
    {
        metric: 'screened',
        labelKey: 'reportsPage.sourceOfHireReport.screenedDistribution',
        shortLabelKey: 'reportsPage.sourceOfHireReport.screened',
        isPercent: false,
    },
    {
        metric: 'hired',
        labelKey: 'reportsPage.sourceOfHireReport.hiresDistribution',
        shortLabelKey: 'reportsPage.sourceOfHireReport.hired',
        isPercent: false,
    },
    {
        metric: 'clickApplyPercent',
        labelKey: 'reportsPage.sourceOfHireReport.clickApplyDistribution',
        shortLabelKey: 'reportsPage.sourceOfHireReport.clickApply',
        isPercent: true,
    },
    {
        metric: 'applyScreenPercent',
        labelKey: 'reportsPage.sourceOfHireReport.applyScreenDistribution',
        shortLabelKey: 'reportsPage.sourceOfHireReport.applyScreen',
        isPercent: true,
    },
    {
        metric: 'overallConversion',
        labelKey: 'reportsPage.sourceOfHireReport.overallConvDistribution',
        shortLabelKey: 'reportsPage.sourceOfHireReport.overallConv',
        isPercent: true,
    },
];

export enum SourceOfHireBarMode {
    BY_SOURCE = 'bySource',
    BY_STAGE = 'byStage',
}

export const SOURCE_OF_HIRE_STAGE_METRICS: SourceOfHireDistributionMetric[] = [
    'applications',
    'hired',
    'screened',
];
